import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { withLegacyRouter } from '../../decorators'
import { bindTargetMixin } from '../shared/FormBuilder'
import { Svg } from '../shared/NarusMaterial'
import { Version } from '../shared/version'

import {
  signin,
  signout,
  startResetPasswordFlow,
  resetPassword,
  submitMFACode,
  setNewPassword,
  loginFormInit,
  userAlreadyAuthenticated,
  setLoginFormScreen,
  setLoading
} from './actions'
import { Button, Input } from './components'

import './styles.scss'

class LoginForm extends bindTargetMixin(React.Component) {
  constructor (props) {
    super(props)
    this.handleSubmitMFACode = this.handleSubmitMFACode.bind(this)
    this.cancelForgotPassword = this.cancelForgotPassword.bind(this)
    this.handleSubmitResetPasswordAndCode = this.handleSubmitResetPasswordAndCode.bind(this)
    this.handleSignIn = this.handleSignIn.bind(this)
    this.handleResetPassword = this.handleResetPassword.bind(this)
    this.handleSetNewPassword = this.handleSetNewPassword.bind(this)
    this.getValues = this.getValues.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.formRef = React.createRef()
  }

  UNSAFE_componentWillMount () {
    if (this.props.auth && this.props.auth.profile) {
      this.props.userAlreadyAuthenticated()
    } else {
      this.props.loginFormInit()
    }
  }

  getValues () {
    return { ...this.state.value }
  }

  cancelForgotPassword () {
    this.setState({
      value: {
        username: this.state.value.username
      }
    })

    signout()
    this.props.setLoginFormScreen('login')
  }

  handleInputChange = (key, value) => {
    this.setState({
      value: {
        ...this.state.value,
        [key]: value
      }
    })
  }

  handleSignIn (e) {
    e.preventDefault()
    const { username, password } = this.getValues()
    this.props.signin(username, password)
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    const { auth } = this.props

    if (nextProps.auth.screen !== this.props.auth.screen) {
      if (auth.username || (auth.password && auth.error)) {
        this.setState({
          value: {
            username: auth.username,
            password: auth.password
          }
        })
      }
    }
  }

  handleSubmitMFACode (e) {
    e.preventDefault()
    let { value: { mfaVerificationCode } } = this.state
    let { auth: { signinEmail }, navigate, location } = this.props

    this.props.submitMFACode(signinEmail, mfaVerificationCode, location.state?.from, navigate)
  }

  handleSubmitResetPasswordAndCode (e) {
    e.preventDefault()
    const {
      value: { verificationCode, newPassword }
    } = this.state
    const {
      auth: { resetPasswordEmail }
    } = this.props

    this.props.resetPassword(resetPasswordEmail, verificationCode, newPassword)
  }

  handleResetPassword (e) {
    e.preventDefault()
    this.props.startResetPasswordFlow(this.state.value.username, true)
  }

  handleSetNewPassword (e) {
    e.preventDefault()
    const {
      value: { newPassword }
    } = this.state
    this.props.setNewPassword(newPassword)
  }

  renderView (screen) {
    const {
      auth: { meta, loading }
    } = this.props

    const loginSchema = {
      inputs: [
        {
          id: 'username',
          name: 'username',
          placeholder: 'Email',
          type: 'email',
          autoComplete: 'email',
          rule: /^(.+)@(.+)$/
        },
        {
          id: 'password',
          name: 'password',
          placeholder: 'Password',
          type: 'password',
          autoComplete: 'current-password'
        }
      ],
      buttons: {
        text: {
          type: 'text',
          label: 'Forgot password?',
          fn: () => this.props.setLoginFormScreen('forgot-password')
        },
        contained: {
          label: 'Sign In',
          fn: this.handleSignIn
        }
      }
    }

    const cancelResetPassword = {
      label: 'Cancel',
      fn: this.cancelForgotPassword
    }

    const forgotPasswordScheme = {
      message: 'To reset password enter your email',
      inputs: [
        {
          id: 'username',
          autoComplete: 'email',
          name: 'username',
          placeholder: 'Email',
          type: 'email',
          rule: /^(.+)@(.+)$/
        }
      ],
      buttons: {
        text: cancelResetPassword,
        contained: {
          label: 'Reset Password',
          fn: this.handleResetPassword
        }
      }
    }

    const newPasswordScheme = {
      message: 'Please provide a new password for your account',
      inputs: [
        {
          id: 'new-password',
          name: 'newPassword',
          placeholder: 'New Password',
          type: 'password',
          autoComplete: 'new-password',
          rule: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
          error: 'Password must be at least 8 characters, must contain an uppercase (A-Z), a lowercase (a-z) and a number (0-9)'
        },
        {
          id: 'password-confirmation',
          name: 'newPasswordConfirmation',
          type: 'password',
          placeholder: 'New Password Confirmation',
          autoComplete: 'new-password',
          rule: new RegExp(`^${this.state?.value?.newPassword ?? ''}$`),
          error: 'Passwords must match'
        }
      ],
      buttons: {
        text: cancelResetPassword,
        contained: {
          label: 'Set Password',
          fn: this.handleSetNewPassword
        }
      }
    }

    const verificationCodeScheme = {
      message: `We have sent you a confirmation code via ${meta?.deliveryMedium === 'EMAIL' ? 'e-mail' : meta?.deliveryMedium} to ${meta?.destination}. Please enter it in the form below as well as a new password`,
      inputs: [
        {
          id: 'verification-code',
          name: 'verificationCode',
          autoComplete: 'one-time-code',
          placeholder: 'Verification Code',
          inputMode: 'numeric'
        },
        {
          id: 'new-password',
          name: 'newPassword',
          autoComplete: 'new-password',
          placeholder: 'New Password',
          rule: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
          type: 'password',
          error: 'Password must be at least 8 characters, must contain an uppercase (A-Z), a lowercase (a-z) and a number (0-9)'
        },
        {
          id: 'password-confirmation',
          name: 'newPasswordConfirmation',
          autoComplete: 'new-password',
          placeholder: 'Password Confirmation',
          type: 'password',
          rule: new RegExp(`^${this.state?.value?.newPassword ?? ''}$`),
          error: 'Passwords must match'
        }
      ],
      buttons: {
        text: cancelResetPassword,
        contained: {
          label: 'Reset Password',
          fn: this.handleSubmitResetPasswordAndCode
        }
      }
    }

    const mfaScheme = {
      message: 'We have sent you a confirmation code via SMS. Please enter it in the form below',
      inputs: [
        {
          type: 'text',
          id: 'verification-code',
          name: 'mfaVerificationCode',
          placeholder: 'Verification Code',
          autoComplete: 'one-time-code',
          inputMode: 'numeric'
        }
      ],
      buttons: {
        text: cancelResetPassword,
        contained: {
          label: 'Sign In',
          fn: this.handleSubmitMFACode
        }
      }
    }

    const schema = {
      'login': loginSchema,
      'forgot-password': forgotPasswordScheme,
      'new-password': newPasswordScheme,
      'verification-code': verificationCodeScheme,
      'mfa-verification-code': mfaScheme
    }

    const containedButton = schema[screen]?.buttons?.contained

    const textButton = schema[screen]?.buttons?.text

    const validateValues = () => {
      return schema?.[screen]?.inputs.some(({ name, rule }) => {
        if (rule) {
          return !this.state?.value?.[name]?.match(rule) ?? true
        }

        return !this.state.value[name]?.length > 0
      })
    }

    const disabled = validateValues()

    return (
      <form
        className='login-form'
        onSubmit={containedButton?.fn}
        ref={this.formRef}
      >
        {schema?.[screen]?.message && (
          <div className='message'>
            {schema[screen].message}
          </div>
        )}
        {schema?.[screen]?.inputs?.map(({ id, name, rule, error, ...rest }) => (
          <Input
            id={id}
            key={id}
            name={name}
            value={this.state?.value?.[name] ?? ''}
            onChange={this.handleInputChange}
            errorMessage={error}
            rule={rule ?? null}
            {...rest}
          />
        ))}
        <div className='buttons'>
          <div className='bottom-bar'>
            {textButton && (
              <button
                type='button'
                className='nh-tiny-login-button'
                onClick={textButton?.fn}
              >
                {textButton?.label}
              </button>
            )}

            {containedButton && (
              <Button
                label={containedButton.label}
                loading={loading}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      </form>
    )
  }

  renderLogic () {
    const { auth: { screen } } = this.props

    return this.renderView(screen)
  }

  render () {
    const {
      auth: { error, flashMessage }
    } = this.props

    return (
      <div className='login'>
        <div className='one-more-wrapper'>
          <div className='login-form-wrapper'>
            <Svg src='logo' />
            {flashMessage && <div className='message flash'>{flashMessage}</div>}
            {error && <div className='message error'>{error}</div>}
            {this.renderLogic()}
          </div>

          <div className='version-wrapper'>
            <Version
              className='text-center pt-6'
              align='center'
            />
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = redux => ({
  auth: redux.auth
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      signin,
      startResetPasswordFlow,
      resetPassword,
      submitMFACode,
      setNewPassword,
      setLoginFormScreen,
      userAlreadyAuthenticated,
      loginFormInit,
      setLoading
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(withLegacyRouter(LoginForm))
